import { IFeedback } from 'api/feedbacks';

/**
 * The search of the feedback list: a word found in any text of a feedback, and a filter of
 * each column. Empty values do not filter.
 */
export interface IFeedbackFilters {
  // The department of the giver
  department: string;
  // The first and last day given (yyyy-mm-dd), both included
  from: string;
  giver: string;
  // negative, neutral or positive
  mood: string;
  page: string;
  // none = without a rating, otherwise a range, e.g. 1-4
  rating: string;
  // Found in the page, location, giver, message or response, case-insensitive
  search: string;
  // new = not handled yet, otherwise a status
  status: string;
  // Found in the message
  text: string;
  to: string;
}

export const NO_FILTERS: IFeedbackFilters = {
  department: '',
  from: '',
  giver: '',
  mood: '',
  page: '',
  rating: '',
  search: '',
  status: '',
  text: '',
  to: '',
};

// The ranges of the overall rating to choose from
export const RATING_RANGES = ['1-4', '5-7', '8-10'];

export const giverOf = (feedback: IFeedback): string =>
  feedback.userName || feedback.userEmail || '–';

// The day of a time in the local time, yyyy-mm-dd, as given by a date field
const dayOf = (time: string): string => {
  const date = new Date(time);
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const contains = (value: null | string | undefined, part: string) =>
  (value ?? '').toLowerCase().includes(part.trim().toLowerCase());

const ratingMatches = (rating: null | number, filter: string): boolean => {
  if (filter === '') {
    return true;
  }
  if (filter === 'none') {
    return rating === null;
  }
  const [min, max] = filter.split('-').map(Number);
  return rating !== null && rating >= min && rating <= max;
};

export const isFiltered = (filters: IFeedbackFilters): boolean =>
  Object.values(filters).some((value) => value.trim() !== '');

export const filterFeedbacks = (
  feedbacks: IFeedback[],
  filters: IFeedbackFilters
): IFeedback[] =>
  feedbacks.filter((feedback) => {
    const day = dayOf(feedback.createdAt);

    return (
      (filters.search.trim() === '' ||
        [
          feedback.page,
          feedback.location,
          giverOf(feedback),
          feedback.departmentName,
          feedback.text,
          feedback.response,
        ].some((value) => contains(value, filters.search))) &&
      (filters.department === '' ||
        (feedback.departmentName ?? '') === filters.department) &&
      (filters.from === '' || day >= filters.from) &&
      (filters.to === '' || day <= filters.to) &&
      (filters.page === '' || feedback.page === filters.page) &&
      (filters.giver === '' || giverOf(feedback) === filters.giver) &&
      (filters.mood === '' || feedback.mood === filters.mood) &&
      ratingMatches(feedback.rating, filters.rating) &&
      (filters.text.trim() === '' || contains(feedback.text, filters.text)) &&
      (filters.status === '' ||
        (filters.status === 'new'
          ? feedback.status === null
          : feedback.status === filters.status))
    );
  });
